import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useLocation } from 'react-router-dom';

import UserAudioQueueContextRaw from '../context/userAudioQueueContext.jsx';
import backgroundMusicManifest from '../data/background_music_manifest.json';
import useMusicPrimaryTab from '../hooks/useMusicPrimaryTab.js';
import { isMusicDisabledPath } from '../utils/musicRoutes.js';
import { addBreadcrumb } from '../monitoring/sentry.js';
import { useAuth } from './Auth0Context.jsx';
import { isTestAccountCachedSync, isTestAccountEmail } from '../constants/testAccounts.js';

export const MUSIC_PRIORITIES = {
  title: 1,
  tavern: 2,
  combat: 3,
  gameover: 4,
};

export const FALLBACK_SLUG = 'title_theme';
export const FALLBACK_VOLUME = 0.4;
const MUSIC_OUTPUT_GAIN = 0.5;
const DEFAULT_USER_MUSIC_VOLUME = 0.2;

export const CROSSFADE_MS = 300;
export const DUCK_IN_MS = 200;
export const DUCK_OUT_MS = 500;
export const DUCK_GAIN = 0.3;

// Music keeps its own keys. `gaiaAudioVolume` / `gaiaAudioMuted` belong to
// speech (`useAudioPlayer`), and both used to read and write them: whichever
// mounted last overwrote the other's value, so opening a surface that uses
// both made the music jump to the narration volume mid-playback.
const MUTE_STORAGE_KEY = 'gaiaMusicMuted';
const VOLUME_STORAGE_KEY = 'gaiaMusicVolume';

const MusicContext = createContext(null);

// Resolve a slug to a concrete /audio/background/X.mp3 path.
// - If the manifest lists ≥2 variant filenames for the slug, pick one at
//   random that isn't `previousFile` (no immediate back-to-back repeats).
// - If the manifest lists exactly one variant, use it deterministically.
// - Otherwise fall back to the legacy single-file convention
//   `/audio/background/<slug>.mp3`.
//
// Exported for tests; the slug-swap effect calls it via slugToSrc().
export const pickVariantFilename = (slug, previousFile = null, rng = Math.random) => {
  const variants = backgroundMusicManifest?.[slug];
  if (!Array.isArray(variants) || variants.length === 0) return `${slug}.mp3`;
  if (variants.length === 1) return variants[0];
  let pick = variants[Math.floor(rng() * variants.length)];
  if (pick === previousFile) {
    // Shift one slot — guarantees a different choice without re-rolling.
    const idx = (variants.indexOf(pick) + 1) % variants.length;
    pick = variants[idx];
  }
  return pick;
};


const readBoolFromStorage = (key) => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(key) === 'true';
};

const readVolumeFromStorage = () => {
  if (typeof window === 'undefined') return DEFAULT_USER_MUSIC_VOLUME;
  const stored = window.localStorage.getItem(VOLUME_STORAGE_KEY);
  if (stored === null) return DEFAULT_USER_MUSIC_VOLUME;
  const parsed = parseFloat(stored);
  if (Number.isNaN(parsed) || parsed < 0 || parsed > 1) return DEFAULT_USER_MUSIC_VOLUME;
  return parsed;
};

const resolveWinner = (claims) => {
  const enabled = [];
  for (const claim of claims.values()) {
    if (claim.enabled) enabled.push(claim);
  }
  if (enabled.length === 0) {
    return { slug: FALLBACK_SLUG, volume: FALLBACK_VOLUME };
  }
  let winner = enabled[0];
  let winnerPriority = MUSIC_PRIORITIES[winner.priority] ?? MUSIC_PRIORITIES.title;
  for (let i = 1; i < enabled.length; i += 1) {
    const c = enabled[i];
    const p = MUSIC_PRIORITIES[c.priority] ?? MUSIC_PRIORITIES.title;
    if (p > winnerPriority || (p === winnerPriority && c.insertOrder > winner.insertOrder)) {
      winner = c;
      winnerPriority = p;
    }
  }
  return { slug: winner.slug, volume: winner.volume };
};

export function MusicProvider({ children }) {
  const audioRef = useRef(null);
  const claimsRef = useRef(new Map());
  const insertCounterRef = useRef(0);

  const [activeSlug, setActiveSlug] = useState(FALLBACK_SLUG);
  const [activeBaseVolume, setActiveBaseVolume] = useState(FALLBACK_VOLUME);
  const [isMuted, setIsMutedState] = useState(() => readBoolFromStorage(MUTE_STORAGE_KEY));
  const [userVolume, setUserVolumeState] = useState(() => readVolumeFromStorage());
  // Duck signal is driven from MusicDuckBridge, which lives inside
  // UserAudioQueueProvider on the routes that have TTS. When MusicProvider
  // sits above (or in routes without) the queue provider, ducked stays
  // false — no TTS, no duck, correct behavior.
  const [ducked, setDuckedState] = useState(false);
  // Paused signal lets callers (notably SessionPausedOverlay during AFK
  // timeout — BOU-1050) stop the looping audio so the tab no longer
  // registers as media-active and stops keeping the window alive past
  // the AFK timer. Resumes on transition back to false.
  const [paused, setPausedState] = useState(false);
  // E2E gate — seeded synchronously from cached Auth0 localStorage so the gate
  // is true from frame 0 for returning test-account sessions (no audio fetch
  // race). MusicE2EGateBridge keeps it in sync for fresh logins, logouts, and
  // account switches via useAuth.
  const [e2eDisabled, setE2EDisabledState] = useState(() => isTestAccountCachedSync());
  // Route gate — admin routes silence music entirely. Seeded synchronously
  // from the address bar so a direct load of /admin never plays a frame;
  // MusicRouteGateBridge keeps it in sync across client-side navigation.
  const [routeDisabled, setRouteDisabledState] = useState(
    () => (typeof window !== 'undefined' && isMusicDisabledPath(window.location?.pathname)),
  );
  // Any hard gate on playback: no src fetch, element muted and paused.
  const playbackDisabled = e2eDisabled || routeDisabled;

  // BOU-1115: cross-tab coordination. Only the primary tab plays music;
  // siblings pause until they regain primary status (via focus or close
  // of the current primary). effectivePaused folds this in alongside the
  // AFK overlay and the test-account e2e gate.
  const { isPrimary } = useMusicPrimaryTab();
  const effectivePaused = paused || !isPrimary || playbackDisabled;

  // Lazily create the single audio element on first render. Attached
  // to document.body via a useEffect (skipped in non-DOM test envs);
  // this lets the E2E bead inspect the element via querySelector.
  if (audioRef.current === null && typeof Audio !== 'undefined') {
    const audio = new Audio();
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = 0;
    if (audio.dataset) audio.dataset.gaiaMusic = 'true';
    audioRef.current = audio;
  }

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || typeof document === 'undefined' || !document.body) return undefined;
    // Guard against test stubs that aren't real DOM nodes.
    if (typeof audio.nodeType !== 'number') return undefined;
    if (audio.parentNode) return undefined;
    document.body.appendChild(audio);
    return () => {
      if (audio.parentNode) audio.parentNode.removeChild(audio);
    };
  }, []);

  const targetVolume = useMemo(() => {
    if (isMuted) return 0;
    const base = activeBaseVolume * userVolume * MUSIC_OUTPUT_GAIN;
    return base * (ducked ? DUCK_GAIN : 1);
  }, [isMuted, activeBaseVolume, userVolume, ducked]);

  // Ramp helpers --------------------------------------------------------------
  const rampStateRef = useRef({ rafId: null, fromVolume: 0, toVolume: 0, startMs: 0, durationMs: 0 });

  const cancelRamp = useCallback(() => {
    const s = rampStateRef.current;
    if (s.rafId !== null) {
      cancelAnimationFrame(s.rafId);
      s.rafId = null;
    }
  }, []);

  const startRamp = useCallback((toVolume, durationMs) => {
    const audio = audioRef.current;
    if (!audio) return;
    cancelRamp();
    const fromVolume = audio.volume;
    const startMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    rampStateRef.current = { rafId: null, fromVolume, toVolume, startMs, durationMs };

    const step = () => {
      const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const elapsed = now - rampStateRef.current.startMs;
      const t = durationMs <= 0 ? 1 : Math.min(1, elapsed / rampStateRef.current.durationMs);
      const v = rampStateRef.current.fromVolume + (rampStateRef.current.toVolume - rampStateRef.current.fromVolume) * t;
      audio.volume = Math.max(0, Math.min(1, v));
      if (t < 1) {
        rampStateRef.current.rafId = requestAnimationFrame(step);
      } else {
        rampStateRef.current.rafId = null;
      }
    };
    rampStateRef.current.rafId = requestAnimationFrame(step);
  }, [cancelRamp]);

  // Resolver — recomputes winner from the registry and pushes activeSlug/baseVolume.
  const recomputeWinner = useCallback(() => {
    const { slug, volume } = resolveWinner(claimsRef.current);
    setActiveSlug((prev) => (prev === slug ? prev : slug));
    setActiveBaseVolume((prev) => (prev === volume ? prev : volume));
  }, []);

  const register = useCallback((claimId, slug, opts = {}) => {
    const existing = claimsRef.current.get(claimId);
    const insertOrder = existing ? existing.insertOrder : (insertCounterRef.current += 1);
    claimsRef.current.set(claimId, {
      claimId,
      slug,
      priority: opts.priority || 'title',
      volume: typeof opts.volume === 'number' ? opts.volume : FALLBACK_VOLUME,
      enabled: opts.enabled !== false,
      insertOrder,
    });
    recomputeWinner();
  }, [recomputeWinner]);

  const unregister = useCallback((claimId) => {
    if (claimsRef.current.delete(claimId)) {
      recomputeWinner();
    }
  }, [recomputeWinner]);

  // Crossfade on slug change. Also handles initial mount: start playing
  // the fallback so every view has music.
  const previousSlugRef = useRef(null);
  const lastVariantBySlugRef = useRef(new Map());
  const lastSrcBySlugRef = useRef(new Map());
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    if (playbackDisabled) {
      audio.muted = true;
      audio.pause();
      return undefined;
    }
    const previous = previousSlugRef.current;
    if (previous === activeSlug) return undefined;

    // Single-phase swap-and-ramp: swap src immediately, start at 0,
    // ramp up to target over CROSSFADE_MS. The audible result is a fade
    // through silence for the outgoing track and a fade-in for the new
    // one — no abrupt cut, total user-perceived swap = CROSSFADE_MS.
    const cached = lastVariantBySlugRef.current.get(activeSlug);
    const variantFile = cached ?? pickVariantFilename(activeSlug, null);
    if (!cached) {
      lastVariantBySlugRef.current.set(activeSlug, variantFile);
    }
    const newSrc = `/audio/background/${variantFile}`;
    previousSlugRef.current = activeSlug;
    if (audio.src.endsWith(newSrc) || audio.src === newSrc) {
      return undefined;
    }
    lastSrcBySlugRef.current.set(activeSlug, newSrc);
    addBreadcrumb({
      category: 'music',
      message: `audio.src reassigned to ${activeSlug}`,
      level: 'info',
      data: { slug: activeSlug, src: newSrc },
    });
    audio.src = newSrc;
    audio.muted = isMuted;
    audio.volume = 0;
    // While paused (e.g. SessionPausedOverlay shown during AFK timeout, or
    // a sibling tab holds the BOU-1115 music lock), swap the src but do not
    // start playback — the paused effect below is the single owner of
    // play/pause transitions in that state.
    if (effectivePaused) return undefined;
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {});
    }
    startRamp(targetVolume, CROSSFADE_MS);
    return undefined;
  }, [activeSlug, isMuted, effectivePaused, startRamp, targetVolume, playbackDisabled]);

  // Volume / duck — ramps audio.volume to targetVolume on every change,
  // unless a slug-swap ramp is already in flight (the swap effect handles
  // its own target).
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Skip volume ramps while paused — there's nothing to fade and we
    // don't want to leave a rAF in flight that would race the resume.
    if (effectivePaused) return;
    if (rampStateRef.current.rafId !== null && rampStateRef.current.toVolume === 0) {
      // mid-crossfade dip — let the swap finish, it will ramp to the new
      // target itself.
      return;
    }
    const duration = ducked ? DUCK_IN_MS : DUCK_OUT_MS;
    startRamp(targetVolume, duration);
  }, [targetVolume, ducked, effectivePaused, startRamp]);

  // Paused — pauses/resumes the audio element. Triggered by
  // SessionPausedOverlay during AFK timeout (BOU-1050) and by the BOU-1115
  // primary-tab election (a sibling tab holds the music lock). On resume,
  // ramp back up to targetVolume so the return isn't an audible jolt.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (effectivePaused) {
      cancelRamp();
      audio.pause();
      return;
    }
    audio.volume = 0;
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {});
    }
    startRamp(targetVolume, CROSSFADE_MS);
  }, [effectivePaused, cancelRamp, startRamp, targetVolume]);

  // Apply muted to the audio element when it changes.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = playbackDisabled || isMuted;
  }, [isMuted, playbackDisabled]);

  // BOU-1204: mirror the resolved slug onto the element as data-active-slug so
  // E2E can assert music continuity across the quest-giver intro. The resolved
  // slug is correct even when playback is gated (test accounts pause/mute the
  // element and the slug-swap effect skips setting src), so this is the
  // reliable signal for the browser, not audio.src.
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && audio.dataset) audio.dataset.activeSlug = activeSlug;
  }, [activeSlug]);

  // Silence immediately on the false→true transition.
  useEffect(() => {
    if (!playbackDisabled) return;
    cancelRamp();
    const audio = audioRef.current;
    if (audio) audio.pause();
  }, [playbackDisabled, cancelRamp]);

  // Persist mute and volume.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(MUTE_STORAGE_KEY, isMuted ? 'true' : 'false');
  }, [isMuted]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(VOLUME_STORAGE_KEY, String(userVolume));
  }, [userVolume]);

  // Final cleanup on provider unmount.
  useEffect(() => {
    return () => {
      cancelRamp();
      const audio = audioRef.current;
      if (audio) {
        try {
          audio.pause();
          audio.src = '';
        } catch {
          // noop
        }
      }
    };
  }, [cancelRamp]);

  const setMuted = useCallback((nextOrUpdater) => {
    setIsMutedState((prev) => (typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : !!nextOrUpdater));
  }, []);

  const setVolume = useCallback((next) => {
    setUserVolumeState(Math.max(0, Math.min(1, Number(next))));
  }, []);

  const setDucked = useCallback((next) => {
    setDuckedState((prev) => {
      const v = typeof next === 'function' ? next(prev) : !!next;
      return v;
    });
  }, []);

  const setPaused = useCallback((next) => {
    setPausedState((prev) => (typeof next === 'function' ? next(prev) : !!next));
  }, []);

  const setE2EDisabled = useCallback((next) => setE2EDisabledState(!!next), []);

  const setRouteDisabled = useCallback((next) => setRouteDisabledState(!!next), []);

  const value = useMemo(() => ({
    register,
    unregister,
    setMuted,
    setVolume,
    setDucked,
    setPaused,
    setE2EDisabled,
    setRouteDisabled,
    isMuted,
    volume: userVolume,
    activeSlug,
    e2eDisabled,
    routeDisabled,
  }), [register, unregister, setMuted, setVolume, setDucked, setPaused, setE2EDisabled, setRouteDisabled, isMuted, userVolume, activeSlug, e2eDisabled, routeDisabled]);

  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
}

// No-op fallback for components rendered outside <MusicProvider> (notably
// unit tests that mount a single component in isolation). Throwing here
// would force every test to wrap in MusicProvider, which provides no
// signal — the component's job is to declare intent, not to verify that
// the manager exists. Real wiring is verified by the MusicProvider
// integration test + the E2E bead.
const NOOP_MUSIC_CONTEXT = Object.freeze({
  register: () => {},
  unregister: () => {},
  setMuted: () => {},
  setVolume: () => {},
  setDucked: () => {},
  setPaused: () => {},
  setE2EDisabled: () => {},
  setRouteDisabled: () => {},
  isMuted: false,
  volume: 1.0,
  activeSlug: FALLBACK_SLUG,
  e2eDisabled: false,
  routeDisabled: false,
});

/**
 * Sits inside UserAudioQueueProvider on routes that have TTS, reads
 * isAudioPlaying, and pushes the duck signal up into MusicProvider's
 * state. Letting MusicProvider read the queue context directly would
 * couple it to UserAudioQueueProvider's mount, which only happens on
 * App routes — MusicProvider needs to wrap everything so /welcome and
 * other auth/landing routes still have a music bed.
 */
export function MusicDuckBridge() {
  const { setDucked } = useMusicContextInternal();
  const queue = useContext(UserAudioQueueContextRaw);
  useEffect(() => {
    setDucked(!!queue?.isAudioPlaying);
  }, [setDucked, queue?.isAudioPlaying]);
  return null;
}

// Sits below Auth0AuthProvider so it can read the authenticated user's email
// and push the e2e-gate signal up into MusicProvider's state. MusicProvider
// cannot call useAuth directly because it wraps Auth0Provider in the tree.
export function MusicE2EGateBridge() {
  const { setE2EDisabled } = useMusicContextInternal();
  const { user, loading } = useAuth();
  const email = user?.email;
  useEffect(() => {
    // Don't flip the gate while auth is still resolving — MusicProvider's
    // useState init seeded it synchronously from cached Auth0 localStorage,
    // and overriding here with isTestAccountEmail(undefined)=false would race
    // the slug-swap effect and trigger a music fetch before login resolves.
    if (loading) return;
    setE2EDisabled(isTestAccountEmail(email));
  }, [setE2EDisabled, email, loading]);
  return null;
}

// Pushes the current pathname's music gate up into MusicProvider's state.
// Bridged rather than read via useLocation inside the provider because
// MusicProvider is also mounted without a Router (unit tests, and any future
// non-routed host), where useLocation throws.
export function MusicRouteGateBridge() {
  const { setRouteDisabled } = useMusicContextInternal();
  const { pathname } = useLocation();
  useEffect(() => {
    setRouteDisabled(isMusicDisabledPath(pathname));
  }, [setRouteDisabled, pathname]);
  return null;
}

// Public hook used by useScopedMusic and by any UI that needs to surface
// mute / volume controls (no such UI ships in this ticket — see scope).
export function useMusicContextInternal() {
  const ctx = useContext(MusicContext);
  return ctx ?? NOOP_MUSIC_CONTEXT;
}

export default MusicContext;
